import SphericalMercator from '@mapbox/sphericalmercator'
import { Feature, FeatureCollection, MultiLineString, Polygon } from 'geojson'
import { Expression, Popup } from 'mapbox-gl'
import React from 'react'
import ReactDOMServer from 'react-dom/server'

import { TripOriginDestination, TripOriginDestination_KindEnum, TripCountInStreetSegment } from 'src/api/gen'

import { Heatmap } from '../constants'

import DestinationPopupContent from './DestinationPopupContent'
import { IQuantileColorScale } from './Legend/QuantileBasedLegend'
import OriginPopupContent from './OriginPopupContent'
import StreetSegmentPopupContent from './StreetSegmentPopupContent'

type StreetSegmentFeatureProperties = {
  id: string
  streetName: string | null
  tripCount: number
} | null

type StreetSegmentFeature = Feature<MultiLineString, StreetSegmentFeatureProperties>

type OriginDestinationFeatureProperties = {
  tripCount: number
} | null

type OriginDestinationFeature = Feature<Polygon, OriginDestinationFeatureProperties>

enum HeatmapLayerId {
  StreetSegments = 'StreetSegments',
  TripDestination = 'TripDestination',
  TripOrigin = 'TripOrigin',
}
const MAX_ZOOM_LEVEL = 22

function removeLayer(map: mapboxgl.Map, layerId: string) {
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId)
  }
  if (map.getSource(layerId)) {
    map.removeSource(layerId)
  }
}

function mapTripDocumentsToFeatureCollections(
  trips: TripOriginDestination[]
): [FeatureCollection<Polygon>, FeatureCollection<Polygon>] {
  const tripOriginFeatures: Feature<Polygon>[] = []
  const tripDestinationFeatures: Feature<Polygon>[] = []

  const merc = new SphericalMercator({ size: 256 })
  for (const trip of trips) {
    const [lon_min, lat_min, lon_max, lat_max] = merc.bbox(trip.x, trip.y, trip.zoom)
    const feature: Feature<Polygon> = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [
          [
            [lon_min, lat_min],
            [lon_min, lat_max],
            [lon_max, lat_max],
            [lon_max, lat_min],
            [lon_min, lat_min],
          ],
        ],
      },
      properties: { tripCount: trip.trip_count },
    }
    if (trip.kind === TripOriginDestination_KindEnum.origin) {
      tripOriginFeatures.push(feature)
    } else {
      tripDestinationFeatures.push(feature)
    }
  }

  return [
    {
      type: 'FeatureCollection',
      features: tripOriginFeatures,
    },
    {
      type: 'FeatureCollection',
      features: tripDestinationFeatures,
    },
  ]
}

function addHeatmapLayerToMap(
  map: mapboxgl.Map,
  layerId: string,
  layerData: FeatureCollection<Polygon>,
  colorScale: IQuantileColorScale | null
) {
  if (layerData.features.length === 0 || !colorScale) {
    return
  }

  const fillColor: Expression = ['step', ['get', 'tripCount'], colorScale.colors[0]]
  for (let i = 1; i < colorScale.colors.length; i++) {
    fillColor.push(colorScale.values[i])
    fillColor.push(colorScale.colors[i])
  }

  map.addSource(layerId, {
    type: 'geojson',
    data: layerData,
  })
  map.addLayer({
    id: layerId,
    source: layerId,
    maxzoom: MAX_ZOOM_LEVEL,
    type: 'fill',
    paint: {
      'fill-color': fillColor,
      'fill-opacity': 0.6,
    },
  })
}

function mapStreetSegmentDocumentsToFeatureCollection(
  tripCountInStreetSegments: TripCountInStreetSegment
): FeatureCollection<MultiLineString> {
  const features: StreetSegmentFeature[] = []

  for (const { key, doc_count } of tripCountInStreetSegments.documents) {
    const streetSegment = tripCountInStreetSegments.street_segments[key]

    if (streetSegment === undefined) {
      // this should not happen now that the street segments and the frequencies are bundled
      // in the same request.
      console.warn(
        `Street segment ${key} is in street_segments count aggregation but not in street segments object, 
        so it will not be possible to display it on the map.`
      )
      continue
    }

    features.push({
      type: 'Feature',
      properties: {
        id: key,
        tripCount: doc_count,
        streetName: streetSegment.name,
      },
      geometry: streetSegment.geojson as MultiLineString,
    })
  }
  return {
    type: 'FeatureCollection',
    features,
  }
}

function addStreetSegmentsToMap(
  map: mapboxgl.Map,
  tripCountInStreetSegments: TripCountInStreetSegment,
  tripHeatmapColorScale: IQuantileColorScale | null
): void {
  if (!tripCountInStreetSegments.documents.length || !tripHeatmapColorScale) {
    return
  }

  map.addSource(HeatmapLayerId.StreetSegments, {
    type: 'geojson',
    data: mapStreetSegmentDocumentsToFeatureCollection(tripCountInStreetSegments),
  })

  const lineColor: Expression = ['step', ['get', 'tripCount'], tripHeatmapColorScale.colors[0]]
  for (let i = 1; i < tripHeatmapColorScale.colors.length; i++) {
    lineColor.push(tripHeatmapColorScale.values[i])
    lineColor.push(tripHeatmapColorScale.colors[i])
  }

  map.addLayer({
    id: HeatmapLayerId.StreetSegments,
    source: HeatmapLayerId.StreetSegments,
    maxzoom: MAX_ZOOM_LEVEL,
    type: 'line',
    paint: {
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        10,
        1, // line-width = 1 when zoom level is 10
        MAX_ZOOM_LEVEL,
        16, // line-width = 16 when zoom level is 22
      ],
      'line-color': lineColor,
      'line-opacity': 0.8,
    },
  })
}

function clearLayers(map: mapboxgl.Map) {
  removeLayer(map, HeatmapLayerId.TripOrigin)
  removeLayer(map, HeatmapLayerId.TripDestination)
  removeLayer(map, HeatmapLayerId.StreetSegments)
}

export function addLayersToMap(
  map: mapboxgl.Map,
  onMapInitialized: () => void,
  trips: TripOriginDestination[],
  tripCountInStreetSegments: TripCountInStreetSegment,
  tripOriginColorScale: IQuantileColorScale | null,
  tripDestinationColorScale: IQuantileColorScale | null,
  tripStreetSegmentColorScale: IQuantileColorScale | null
): void {
  clearLayers(map)

  const [tripOriginFeatureCollection, tripDestinationFeatureCollection] = mapTripDocumentsToFeatureCollections(trips)

  addHeatmapLayerToMap(map, HeatmapLayerId.TripOrigin, tripOriginFeatureCollection, tripOriginColorScale)
  addHeatmapLayerToMap(map, HeatmapLayerId.TripDestination, tripDestinationFeatureCollection, tripDestinationColorScale)

  addStreetSegmentsToMap(map, tripCountInStreetSegments, tripStreetSegmentColorScale)

  // When all loading operations are done, the map enters an idle state
  map.on('idle', onMapInitialized)
}

function showLayer(map: mapboxgl.Map, id: HeatmapLayerId) {
  if (map.getLayer(id)) {
    map.setLayoutProperty(id, 'visibility', 'visible')
  }
}

function hideLayer(map: mapboxgl.Map, id: HeatmapLayerId) {
  if (map.getLayer(id)) {
    map.setLayoutProperty(id, 'visibility', 'none')
  }
}

export function displayOnlyLayerCorrespondingToHeatmap(map: mapboxgl.Map, heatmap: Heatmap) {
  switch (heatmap) {
    case Heatmap.Origin:
    default:
      showLayer(map, HeatmapLayerId.TripOrigin)
      hideLayer(map, HeatmapLayerId.TripDestination)
      hideLayer(map, HeatmapLayerId.StreetSegments)
      break
    case Heatmap.Destination:
      showLayer(map, HeatmapLayerId.TripDestination)
      hideLayer(map, HeatmapLayerId.TripOrigin)
      hideLayer(map, HeatmapLayerId.StreetSegments)
      break
    case Heatmap.Trips:
      showLayer(map, HeatmapLayerId.StreetSegments)
      hideLayer(map, HeatmapLayerId.TripOrigin)
      hideLayer(map, HeatmapLayerId.TripDestination)
      break
  }
}

export function setupStreetSegmentPopup(map: mapboxgl.Map) {
  const popup = new Popup({
    closeButton: false,
    closeOnClick: false,
    anchor: 'left',
    offset: 50,
  })
  map.on('mouseenter', HeatmapLayerId.StreetSegments, (event: mapboxgl.MapLayerMouseEvent) => {
    map.getCanvas().style.cursor = 'pointer'

    // Populate the popup and set its coordinates based on the cursor position
    const features = event.features as StreetSegmentFeature[]
    const { ...props } = features[0].properties
    const reactElement = React.createElement(StreetSegmentPopupContent, props)
    const html = ReactDOMServer.renderToStaticMarkup(reactElement)
    popup.setHTML(html).setLngLat(event.lngLat).addTo(map)
  })
  map.on('mouseleave', HeatmapLayerId.StreetSegments, () => {
    map.getCanvas().style.cursor = ''
    popup.remove()
  })
  map.on('mousemove', HeatmapLayerId.TripOrigin, (event: mapboxgl.MapLayerMouseEvent) => {
    map.getCanvas().style.cursor = 'pointer'
    const features = map.queryRenderedFeatures(event.point) as OriginDestinationFeature[]
    const { ...props } = features[0].properties
    const reactElement = React.createElement(OriginPopupContent, props)
    const html = ReactDOMServer.renderToStaticMarkup(reactElement)
    popup.setHTML(html).setLngLat(event.lngLat).addTo(map)
  })
  map.on('mouseleave', HeatmapLayerId.TripOrigin, () => {
    map.getCanvas().style.cursor = ''
    popup.remove()
  })
  map.on('mousemove', HeatmapLayerId.TripDestination, (event: mapboxgl.MapLayerMouseEvent) => {
    map.getCanvas().style.cursor = 'pointer'
    const features = map.queryRenderedFeatures(event.point) as OriginDestinationFeature[]
    const { ...props } = features[0].properties
    const reactElement = React.createElement(DestinationPopupContent, props)
    const html = ReactDOMServer.renderToStaticMarkup(reactElement)
    popup.setHTML(html).setLngLat(event.lngLat).addTo(map)
  })
  map.on('mouseleave', HeatmapLayerId.TripDestination, () => {
    map.getCanvas().style.cursor = ''
    popup.remove()
  })
}
